/**
 * @file product-form.ts
 * @description Тип формы редактирования товара + хелперы fromProduct / toPayload.
 * Унифицированно используется всеми секциями ProductEditPage.
 *
 * Р3в (v0.19.64): «Ключевые запросы» (SEO11) и галочка «Переадресовать со
 * старого адреса» (SB131, по умолчанию да — уходит только при смене адреса
 * сохранённого товара).
 */

import type {
  CatalogProduct,
  CreateProductInput,
  ProductItemKindValue,
} from "../../../lib/api-catalog";
import {
  attributesForPayload,
  cleanAttributes,
  newAttributeRow,
  parseAttributes,
  type ProductAttributeRow,
} from "./product-attributes";
import { keywordsInput, keywordsOf, type SeoKeywordsValue } from "../../../lib/site-seo/keywords";

/**
 * В форме `isPublishedOnSite` храним как строку `"inherit" | "true" | "false"`
 * чтобы радиокнопкам было удобнее. Конверсия в backend-значение — в toPayload.
 */
export type PublishOnSiteRadio = "inherit" | "true" | "false";

export interface ProductFormState {
  categoryId: string;
  displayName: string;
  slug: string;
  shortDescription: string;
  description: string;
  metaTitle: string;
  metaDescription: string;
  // Подробные
  composition: string;
  shelfLifeDays: string;
  storageConditions: string;
  /** phase19.16 Р2а (SB33) — характеристики «название — значение» по порядку. */
  attributes: ProductAttributeRow[];
  /** FK на справочник Producer (заменяет inline producerName/Address/Inn). */
  producerId: string;
  // Регуляторные
  allergensCsv: string;
  badWarning: boolean;
  ageRestriction: string;
  notForConsumption: boolean;
  declarationNumber: string;
  // Связи
  tagIds: string[];
  // phase14.0.2 — таксономия / стикеры
  isPublishedOnSite: PublishOnSiteRadio;
  itemKind: "" | ProductItemKindValue;
  /** phase19.22 (W22) — свой вид товара; "" — «как в категории». */
  productKindId: string;
  /** Р3в (SEO11) — ключевые запросы: заметка, на сайт не выводится. */
  keywords: SeoKeywordsValue;
  /** Р3в (SB131) — переадресовать со старого адреса при смене адреса. */
  redirectFromOldSlug: boolean;
}

/** Адрес сохранённого товара меняется в форме (показать галочку переадресации); `saved` — товар или его адрес. */
export function productSlugChanged(form: ProductFormState, saved: Pick<CatalogProduct, "slug"> | string | null): boolean {
  const slug = typeof saved === "string" ? saved : (saved?.slug ?? null);
  return !!slug && form.slug.trim() !== "" && form.slug.trim() !== slug;
}

export function fromProduct(p: CatalogProduct | null): ProductFormState {
  return {
    categoryId: p?.categoryId ?? "",
    displayName: p?.displayName ?? "",
    slug: p?.slug ?? "",
    shortDescription: p?.shortDescription ?? "",
    description: p?.description ?? "",
    metaTitle: p?.metaTitle ?? "",
    metaDescription: p?.metaDescription ?? "",
    composition: p?.composition ?? "",
    shelfLifeDays: p?.shelfLifeDays?.toString() ?? "",
    storageConditions: p?.storageConditions ?? "",
    attributes: parseAttributes(p?.attributes).map((a) => newAttributeRow(a.key, a.value)),
    producerId: p?.producerId ?? "",
    allergensCsv: p?.allergens.join(", ") ?? "",
    badWarning: p?.badWarning ?? false,
    ageRestriction: p?.ageRestriction?.toString() ?? "",
    notForConsumption: p?.notForConsumption ?? false,
    declarationNumber: p?.declarationNumber ?? "",
    tagIds: p?.tagIds ?? [],
    isPublishedOnSite:
      p?.isPublishedOnSite === null || p?.isPublishedOnSite === undefined
        ? "inherit"
        : p.isPublishedOnSite
          ? "true"
          : "false",
    itemKind: p?.itemKind ?? "",
    productKindId: p?.productKindId ?? "",
    keywords: keywordsOf(p),
    redirectFromOldSlug: true,
  };
}

/**
 * `productKindId` для запроса. Новый товар: свой вид или `undefined` (сервер
 * возьмёт вид категории, нет его — 422 `product_kind_required`). Правка: поле
 * уходит, только если вид поменяли — иначе `null` у товара старой категории
 * без вида ловил бы тот же 422 на любом сохранении (сервер проверяет сброс в
 * «как в категории» при каждом `null`).
 */
function kindForPayload(
  form: ProductFormState,
  original: CatalogProduct | null,
): string | null | undefined {
  if (!original) return form.productKindId || undefined;
  if ((original.productKindId ?? "") === form.productKindId) return undefined;
  return form.productKindId || null;
}

/**
 * `producerId` для запроса. Новый товар: id или `undefined`. Правка: пусто —
 * `null` (сервер снимает производителя; `undefined` у сервера — «не трогать»),
 * но только если у товара он был: иначе поле не отправляем вовсе
 * (замечание владельца 2026-10-19, пункт «Отсутствует»).
 */
function producerForPayload(
  form: ProductFormState,
  original: CatalogProduct | null,
): string | null | undefined {
  const id = form.producerId.trim();
  if (id) return id;
  return original?.producerId ? null : undefined;
}

export function toPayload(
  form: ProductFormState,
  original: CatalogProduct | null = null,
): CreateProductInput {
  const allergens = form.allergensCsv
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    categoryId: form.categoryId,
    displayName: form.displayName.trim(),
    slug: form.slug.trim() || undefined,
    shortDescription: form.shortDescription.trim() || undefined,
    description: form.description.trim() || undefined,
    // TD-634: стёртый заголовок — null (сервер очищает поле), а не «без изменений».
    metaTitle: form.metaTitle.trim() || (original?.metaTitle ? null : undefined),
    metaDescription: form.metaDescription.trim() || undefined,
    composition: form.composition.trim() || undefined,
    shelfLifeDays: form.shelfLifeDays ? parseInt(form.shelfLifeDays, 10) : undefined,
    storageConditions: form.storageConditions.trim() || undefined,
    allergens: allergens.length > 0 ? allergens : undefined,
    badWarning: form.badWarning,
    ageRestriction: form.ageRestriction ? parseInt(form.ageRestriction, 10) : undefined,
    notForConsumption: form.notForConsumption,
    producerId: producerForPayload(form, original),
    declarationNumber: form.declarationNumber.trim() || undefined,
    tagIds: form.tagIds,
    attributes: attributesForPayload(form.attributes, original?.attributes, !original),
    isPublishedOnSite:
      form.isPublishedOnSite === "inherit"
        ? null
        : form.isPublishedOnSite === "true",
    itemKind: form.itemKind === "" ? null : form.itemKind,
    productKindId: kindForPayload(form, original),
    ...keywordsInput(form.keywords),
    ...(productSlugChanged(form, original) ? { redirectFromOldSlug: form.redirectFromOldSlug } : {}),
  };
}

/** Пустой абзац редактора описания и пустая строка — одно и то же. */
function normalizeHtml(html: string): string {
  const compact = html.replace(/>\s+</g, "><").trim();
  return compact === "<p></p>" ? "" : compact;
}

/**
 * Значения формы в виде для сравнения: строки без крайних пробелов, теги и
 * аллергены — без учёта порядка и пустых, описание — без пустого абзаца,
 * характеристики — по порядку, без пустых строк.
 */
function normalizedForm(form: ProductFormState): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  // Ключи по алфавиту — сравнение не зависит от порядка полей в объекте.
  for (const key of Object.keys(form).sort() as Array<keyof ProductFormState>) {
    const value = form[key];
    out[key] = typeof value === "string" ? value.trim() : value;
  }
  out.description = normalizeHtml(form.description);
  out.tagIds = [...form.tagIds].sort();
  // Характеристики — без id строк и без пустых строк (их не сохраняем).
  out.attributes = cleanAttributes(form.attributes);
  out.allergensCsv = form.allergensCsv
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .sort()
    .join(",");
  return out;
}

/**
 * Есть ли несохранённые изменения — сравнением с загруженным товаром, а не
 * флагом «что-то вызвало onChange» (приёмка QA-5 D13: ложное «Есть
 * несохранённые изменения» сразу после открытия товара).
 */
export function productFormChanged(form: ProductFormState, baseline: ProductFormState): boolean {
  return JSON.stringify(normalizedForm(form)) !== JSON.stringify(normalizedForm(baseline));
}

/** Опции «Роли в производстве» (`itemKind`, W22 — ось «роль», не «вид»). Без эмодзи (SC29). */
export const ITEM_KIND_OPTIONS: Array<{ value: ProductItemKindValue; label: string }> = [
  { value: "RAW_MATERIAL", label: "Сырьё" },
  { value: "WORK_IN_PROGRESS", label: "Полуфабрикат" },
  { value: "FINISHED_GOOD", label: "Готовый товар" },
  { value: "PACKAGING", label: "Упаковка и расходники" },
  { value: "SERVICE", label: "Услуга" },
  { value: "DIGITAL", label: "Цифровой товар" },
  { value: "BUNDLE", label: "Комплект-набор" },
];
